/* eslint-disable no-useless-escape */

/* global google */

import $ from 'jquery'
import each from 'lodash/each'

export default {
  // language=HTML (for PhpStorm)
  template: `
    <div class="dealer-locator" :class="{ 'map-hidden': !isMapVisible } ">
      <div class="dealer-locator__sidebar">
        <div class="dealer-locator__form">
          <label>
            Find near zip code:
            <input ref="zip" type="text" v-model="zip" pattern="\d+" placeholder="Zip Code" maxlength="5">
          </label>
        </div>
        <ul class="dealer-locator__list">
          <li v-show="searching" class="searching">
            <ui-progress-circular :size="32" :indeterminate="true"></ui-progress-circular>
          </li>
          <li v-if="noResults" class="no-results">
            No results
          </li>
          <li v-if="results.length" class="summary">
            Found <b>{{ results.length }}</b> near <b>{{ zip }}</b>
          </li>
          <li
            class="dealer"
            v-for="dealer, resultIdx in results"
            :key="dealer.id"
            @click="focusMarker(resultIdx)"
            @mouseenter="animateMarker(resultIdx)"
            @mouseleave="stopAnimateMarker(resultIdx)"
          >
            <div class="dealer__num">{{ resultIdx + 1 }}</div>
            <div class="dealer__info">
              <div class="dealer__name">
                <a v-if="dealer.website" :href="dealer.website" target="_blank" @click.stop>{{ dealer.name }}</a>
                <span v-else>{{ dealer.name }}</span>
              </div>
              <div class="dealer__phone" v-if="dealer.phone">{{ dealer.phone }}</div>
              <div class="dealer__address" v-if="dealer.address">{{ dealer.address }}</div>
            </div>
          </li>
        </ul>
      </div>
      <div v-show="isMapVisible" class="dealer-locator__map">
        <div ref="map" id="map"></div>
      </div>
    </div>
  `,
  props: {
    searchUrl: {
      type: String,
      required: true,
    },
    initialZip: {
      type: String,
      required: false,
      default() {
        return ''
      },
    },
  },
  data() {
    return {
      zip: this.initialZip,
      results: [],
      expanded: false,
      searching: false,
      noResults: false,
    }
  },
  computed: {
    isMapVisible() {
      return this.expanded
    },
  },
  watch: {
    zip(newValue) {
      this.results = []
      if (newValue.length >= 3) {
        this.search()
      } else {
        this.noResults = false
      }
    },
    results(dealers) {
      this.resetMarkers()

      if (!dealers.length) {
        this.resetMap()
        return
      }

      this.expanded = true

      this.$nextTick(() => {
        // Map may have been hidden and not rendered properly:
        google.maps.event.trigger(this.map, 'resize')

        let bounds = new google.maps.LatLngBounds()

        each(dealers, (dealer) => {
          const position = new google.maps.LatLng(parseFloat(dealer.lat), parseFloat(dealer.lng))
          bounds = bounds.extend(position)
        })

        // Keeps it from being too zoomed in
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
          const extendPoint1 = new google.maps.LatLng(
            bounds.getNorthEast().lat() + 0.005,
            bounds.getNorthEast().lng() + 0.005,
          )
          const extendPoint2 = new google.maps.LatLng(
            bounds.getNorthEast().lat() - 0.005,
            bounds.getNorthEast().lng() - 0.005,
          )
          bounds.extend(extendPoint1)
          bounds.extend(extendPoint2)
        }

        this.map.fitBounds(bounds)

        each(dealers, (dealer, resultIdx) => {
          setTimeout(() => {
            const position = new google.maps.LatLng(parseFloat(dealer.lat), parseFloat(dealer.lng))

            const marker = new google.maps.Marker({
              position,
              label: (resultIdx + 1).toString(),
              map: this.map,
              // animation: google.maps.Animation.DROP,
            })

            const nameHtml = `
              <div class="map-marker__dealer__name">
                ${dealer.website ? `<a href="${dealer.website}" target="_blank">${dealer.name}</a>` : dealer.name}
              </div>
            `

            const phoneHtml = dealer.phone ? `
              <div class="map-marker__dealer__phone">${dealer.phone}</div>
            ` : ''

            const addressHtml = dealer.address ? `
              <div class="map-marker__dealer__address">${dealer.address}</div>
            ` : ''

            const infoHtml = `
              <div class="map-marker__dealer">
                ${nameHtml}
                ${phoneHtml}
                ${addressHtml}
              </div>
            `

            marker.addListener('click', () => {
              this.mapInfoWindow.setContent(infoHtml)
              this.mapInfoWindow.open(this.map, marker)
            })

            this.markers.push(marker)
          }, resultIdx * 50)
        })
      })
    },
  },
  methods: {
    search() {
      this.searching = true
      this.noResults = false

      if (this.request) {
        this.request.abort()
      }

      this.request = $.ajax({
        type: 'POST',
        url: this.searchUrl,
        data: {
          zip: this.zip,
        },
        dataType: 'json',
      })
        .done((resp) => {
          this.results = resp.results
          this.noResults = resp.results.length < 1
        })
        .always(() => {
          this.searching = false
        })
    },

    resetMap() {
      this.map.setCenter(new google.maps.LatLng(37.2758251, -104.6538081))
      this.map.setZoom(4)
    },

    resetMarkers() {
      each(this.markers, (marker) => marker.setMap(null))
      this.markers = []
    },

    focusMarker(idx) {
      if (this.markers[idx]) {
        this.map.panTo(this.markers[idx].getPosition())
      }
    },

    animateMarker(idx) {
      if (this.markers[idx]) {
        this.markers[idx].setAnimation(google.maps.Animation.BOUNCE)
      }
    },

    stopAnimateMarker(idx) {
      if (this.markers[idx]) {
        this.markers[idx].setAnimation(null)
      }
    },
  },
  created() {
    this.markers = []
    this.request = null
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.zip.focus()
      this.map = new google.maps.Map(this.$refs.map)
      this.resetMap()

      this.mapInfoWindow = new google.maps.InfoWindow()

      if (this.initialZip) {
        this.expanded = true
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          this.search()
        })
      }
    })
  },
}
